import { useEffect, useState } from 'react';
import { useLocation, useSearch } from 'wouter';
import { ShieldCheck, AlertCircle, Loader2 } from 'lucide-react';
import { api, ApiCallError } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';

/**
 * Landing del magic-link: el bot manda al cliente una URL
 *   ${BASE_URL}/app/auth?token=...
 * Esta página es pura SPA (no consume el token al renderizarse). El
 * usuario hace click en "Entrar al panel" → POST /api/app/auth/verify
 * con el token en el body → el server consume el token y crea sesión.
 *
 * Los link-preview crawlers de WhatsApp/Telegram GETean esta página
 * pero no disparan el POST, así que el token sigue intacto cuando el
 * usuario humano abre el link.
 */
export function AppAuthPage() {
  const [, navigate] = useLocation();
  const search = useSearch();
  const params = new URLSearchParams(search);
  const token = params.get('token') ?? '';
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Si no hay token, mandamos a la página de instrucciones
  useEffect(() => {
    if (!token) navigate('/app/login');
  }, [token, navigate]);

  async function onContinue() {
    setError(null);
    setLoading(true);
    try {
      await api('/api/app/auth/verify', {
        method: 'POST',
        body: JSON.stringify({ token }),
      });
      navigate('/app');
    } catch (err) {
      if (err instanceof ApiCallError && err.status === 409) {
        setError('Hay una sesión de admin abierta. Cerrá sesión del admin primero.');
      } else {
        setError(
          'Este link ya no es válido. Pedile al bot un nuevo link (los links duran 15 minutos y se usan una sola vez).'
        );
      }
    } finally {
      setLoading(false);
    }
  }

  if (!token) return null;

  return (
    <div className="min-h-screen flex items-center justify-center bg-muted px-4 py-8">
      <Card className="w-full max-w-md">
        <CardHeader className="text-center">
          <div className="mx-auto mb-3 flex size-12 items-center justify-center rounded-full bg-primary/10 text-primary">
            <ShieldCheck className="size-6" />
          </div>
          <CardTitle className="text-2xl">Entrar al panel</CardTitle>
          <CardDescription>
            Confirmá que sos vos para abrir tu cuenta en Actuo.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {error && (
            <div className="flex items-start gap-2 rounded-md border border-destructive/30 bg-destructive/10 p-3 text-sm text-destructive">
              <AlertCircle className="size-4 mt-0.5 shrink-0" />
              <span>{error}</span>
            </div>
          )}

          <Button className="w-full" onClick={onContinue} disabled={loading}>
            {loading ? (
              <>
                <Loader2 className="animate-spin" />
                Validando…
              </>
            ) : (
              'Entrar al panel'
            )}
          </Button>

          <p className="text-xs text-muted-foreground text-center">
            Si no fuiste vos quien pidió este link, ignoralo. Caduca solo en 15 minutos.
          </p>
        </CardContent>
      </Card>
    </div>
  );
}
